import { Settings as SettingsIcon } from 'lucide-react';

import { definePanel } from '@/app/definePanel';
import type { PanelWidth } from '@/types/panels';

import { WEB_PAGE_TAB_ID } from './tabs';

export default definePanel({
    id: 'camera_settings',
    title: 'Camera Settings',
    icon: SettingsIcon,
    // The form wants 800px: a 240px rail beside a section pane wide enough to
    // keep labels next to their controls. `keepsDefaultWidth` makes that a claim on the
    // panels to its right rather than a wish — the preview is dropped rather
    // than the form squeezed (see selectVisible). Compact mode still reaches
    // down to 480px when the stage genuinely cannot pay, and the form never
    // stretches past 800 merely because the stage grew.
    width: { min: 480, default: 800, max: 800, fill: false, keepsDefaultWidth: true },
    // The Web page tab frames a camera's own page, laid out for a desktop
    // browser window: with the preview closed it takes the whole stage, beside
    // the preview it asks for 1200px and may be dragged wider still. A dragged
    // width is clamped by the rules in force, so one made here falls back to
    // 800 on the other tabs and returns on the next visit.
    getWidth: (props, row): Partial<PanelWidth> | undefined => {
        if (props.settingsTab !== WEB_PAGE_TAB_ID) {
            return undefined;
        }
        return row.some((openPanel) => openPanel.id === 'camera_view')
            ? { default: 1200, max: Infinity }
            : { max: Infinity, fill: true, keepsDefaultWidth: false };
    },
    modes: ['settings'], // settings-only — closes when switching to view
    themes: 'any',
    order: 4,
    // The live preview is the form's auto-child, on the same terms as any
    // list's: it opens beside the form when the stage has room for both and is
    // left closed when it hasn't (see resolveAutoChild). On a narrow stage the
    // form therefore arrives alone, and the section header's Preview button is
    // how the operator asks for the picture — which then takes the stage.
    //
    // The cameras list is deliberately NOT pulled in. Reaching the form from
    // the player's own Settings button means "configure the camera I am watching"
    // — pulling a camera tree in alongside it would be answering a question
    // nobody asked, and would push the preview out on a narrow stage. The nav
    // rail's Camera settings button wants the whole workflow instead, and asks
    // for the list explicitly (see NavRail's `also` option).
    autoChild: 'camera_view',
    // Host owns its own two-pane header layout — skip the default chrome.
    isHeaderless: true,
    // Mutual exclusions live in src/app/exclusions.ts (settings-mode list
    // slot + settings-detail clique).
    // cameraId is the shared selection with camera_view; settingsTab names the
    // tab, so a reload or a shared link opens the same camera on the same tab.
    urlSyncedProps: ['cameraId', 'settingsTab'],
    // Either one swaps the form out from under its unsaved edits.
    subjectProps: ['cameraId', 'settingsTab'],
    // Auto-opens via camera_view's `autoOpenIds` — not a rail button.
    hasNavButton: false,
});
